import { dismissedUntil } from './ios-app.js';

/**
 * Право показать предложение установить Android-приложение из Google Play.
 *
 * Правила — те же, что у iPhone (lib/ios-app.ts), и ломаются так же молча: баннер либо
 * не покажется тому, кому нужен, либо покажется тому, у кого приложение уже стоит.
 * Перенесено из кампуса (`../Campus/CampusImage/web/src/lib/android-app.ts`).
 *
 * Нативной плашки, как Smart App Banner в Safari, для приложения из Google Play Chrome не
 * рисует (`related_applications` в манифесте работает только в паре с PWA-установкой),
 * поэтому собственный баннер здесь — единственный канал на всех Android-браузерах.
 */

/** Отказ помнится отдельно от iPhone-баннера: это разные предложения. */
export const ANDROID_DISMISS_KEY = 'mm.android-app.dismissed';

/**
 * Встроенный WebView чужого приложения (мессенджер, соцсеть) помечает строку агента `; wv)`.
 * Оттуда переход в Google Play работает плохо или не работает вовсе, а человек и так
 * откроет ссылку в браузере, если захочет.
 */
const WEBVIEW_MARKER = '; wv)';

/** Тот же маркер, что у iPhone: сайт, открытый ВНУТРИ приложения MathMind. */
const IN_APP_MARKER = 'MathMindApp';

export interface AndroidProbe {
  readonly userAgent: string;
  /** Сайт установлен как веб-приложение и запущен с рабочего стола. */
  readonly displayModeStandalone: boolean;
}

export interface AndroidOfferInput {
  /** Адрес карточки в Google Play из манифеста. Пусто = приложения у деплоя нет. */
  readonly storeUrl: string;
  readonly probe: AndroidProbe;
  readonly storage: Pick<Storage, 'getItem'> | null;
  readonly now: number;
}

/**
 * ⚠️ Планшеты не исключаются, в отличие от iPad: Google Play сам скажет, совместимо ли
 * приложение с устройством, а строка агента планшета Android от телефона надёжно не
 * отличается.
 */
export function shouldOfferAndroidApp(input: AndroidOfferInput): boolean {
  if (input.storeUrl === '') return false;
  const ua = input.probe.userAgent;
  if (!/Android/u.test(ua)) return false;
  if (ua.includes(IN_APP_MARKER)) return false;
  if (ua.includes(WEBVIEW_MARKER)) return false;
  if (input.probe.displayModeStandalone) return false;
  return input.now >= dismissedUntil(input.storage, ANDROID_DISMISS_KEY);
}

export function readAndroidProbe(): AndroidProbe {
  let displayModeStandalone = false;
  try {
    displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    // Движок без matchMedia — считаем, что это обычная вкладка.
  }
  return { userAgent: navigator.userAgent, displayModeStandalone };
}
